/**
 * Utility functions for generating meta tags and SEO data
 */

/**
 * Get the absolute URL for the current page
 * @param path - Relative path (defaults to current path)
 * @returns Absolute URL
 */
export const getAbsoluteUrl = (path?: string): string => {
  if (typeof window === "undefined") {
    return "";
  }

  const currentPath = path || window.location.pathname;
  const protocol = window.location.protocol;
  const host = window.location.host;

  return `${protocol}//${host}${currentPath}`;
};

/**
 * Truncate text to a specified length for meta descriptions
 * @param text - Text to truncate
 * @param maxLength - Maximum length (default: 155)
 * @returns Truncated text
 */
export const truncateDescription = (text: string, maxLength: number = 155): string => {
  if (!text) return "";

  // Strip HTML tags
  const textWithoutHtml = text.replace(/<[^>]*>/g, "").trim();

  if (textWithoutHtml.length <= maxLength) {
    return textWithoutHtml;
  }

  // Truncate at word boundary
  const truncated = textWithoutHtml.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");

  if (lastSpace > 0) {
    return truncated.substring(0, lastSpace) + "...";
  }

  return truncated.substring(0, maxLength - 3) + "...";
};

/**
 * Get the primary image URL from a product
 * @param product - Product object
 * @param placeholder - Placeholder image URL
 * @returns Image URL
 */
export const getProductPrimaryImage = (product: any, placeholder: string): string => {
  if (!product?.images || product.images.length === 0) {
    return placeholder;
  }

  // Find primary image or use first image
  const primaryImage = product.images.find((img: any) => img.isPrimary) || product.images[0];
  return primaryImage.url || placeholder;
};
